import React, { useEffect, useState } from 'react';
import { InformationCircleIcon, ClockIcon, UserGroupIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { getResourceBySlug } from '../../../lib/db';

const CultureOffsiteKeyInfo = () => {
  const [resource, setResource] = useState(null);
  
  useEffect(() => {
    getResourceBySlug('culture-offsite').then(setResource);
  }, []);

  return (
    <div className="max-w-none text-gray-300">
      <PlaybookHero
        title={resource?.title || "Culture Playbook: Team Offsite – Culture Co-Creation"}
        subtitle={resource?.description || "A step-by-step guide to running a culture co-creation offsite for effective team alignment"}
        category={resource?.category || "Culture & Team Building"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "10 min read"}
        type={resource?.type}
      />

      {/* Compelling quote block */}
      <div className="my-8 p-6 bg-gray-800/30 rounded-lg border-l-4 border-primary-light">
        <p className="text-lg italic text-gray-200">
          Culture doesn't have to be dismissed as intangible or reduced to just a set of 'hand wavy' values on your career site. 
          It is tangible and can be managed just like a product.
        </p>
      </div>

      {/* This Guide is for you if section */}
      <PlaybookSection
        title="This Playbook is for you if..."
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <div className="space-y-4">
          <div className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <p>You've crafted values and behaviours but are struggling to operationalise them and make them stick</p>
          </div>
          <div className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <p>You can see the need for a change in how folks work together but aren't sure where to start</p>
          </div>
          <div className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <p>You've got an offsite planned and want to do something meaningful that will bring people closer together through alignment and engagement</p>
          </div>
        </div>
      </PlaybookSection>

      {/* Guide Overview / Metrics */}
      <PlaybookSection
        title="Guide Overview"
        icon={<ClockIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <PlaybookMetrics
          metrics={[
            { label: 'Difficulty', value: 'Medium' },
            { label: 'Timeline', value: '4 Hours Prep, 4 hours runtime' },
            { label: 'Team Size', value: 'All, works best for 30-150 people' },
            { label: 'Why Use', value: 'Alignment on your collective ways of working that will drive business success and operational excellence. Team-building is a by-product, not the focus.' },
            { label: 'When to Use', value: 'When you need to reset on "how" we do our best work. (Lots of new people have joined, old ways of working no longer fit for purpose at scale, or starting to see behaviors that don\'t serve your needs)' }
          ]}
        />
      </PlaybookSection>

      {/* Watch This section */}
      <PlaybookSection
        title="Watch This"
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <VideoEmbed
          src="https://www.loom.com/embed/390989b5b387406e85f17f865c0f69c0"
          title="Culture Co-Creation Overview"
        />
      </PlaybookSection>

      {/* What Is This section */}
      <PlaybookSection
        title="What Is This"
        icon={<QuestionMarkCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <div className="space-y-8">
        <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/culture-as-product.png"
            alt="Culture as a Product Concept"
            caption="Culture can and should be managed intentionally like a product"
            className="rounded-lg border border-gray-700 w-full"
            
          />

          <p className="text-gray-300 leading-relaxed">
            I've worked with a bunch of leadership teams who 'switch off' immediately if you try and engage them in a conversation about culture. 7 years of being metaphorically 'patted on the head' gave me a bit of a complex about this. But what I learned (eventually!) is that it's a loaded word and most people are starting from pretty different definitions/understanding. Anything from 'oh he means team building' to 'this is about how we furnish the office' to 'Ah right, ok this is some HR things'.
          </p>

    

          <p className="text-gray-300 leading-relaxed">
            But I guess the point is: I think it's important to define it first.
          </p>

          <p className="text-gray-300 leading-relaxed">
            I like this definition of culture for its simplicity (Deal & Kennedy, 2000).
          </p>

          <div className="my-6 p-6 bg-gray-800/30 rounded-lg border-l-4 border-primary-light">
            <p className="text-lg italic text-gray-200">
              'the way things are done around here.'
            </p>
          </div>

          <p className="text-gray-300 leading-relaxed">
            If we anchor that for how we think about today's culture, then I propose tomorrow's culture is:
          </p>

          <div className="my-6 p-6 bg-gray-800/30 rounded-lg border-l-4 border-primary-light">
            <p className="text-lg italic text-gray-200">
              'how we will do our best work together.'
            </p>
          </div>

          <p className="text-gray-300 leading-relaxed">
            If a team isn't thinking about culture like this, I say: folks, welcome to the land of opportunity! Culture doesn't have to be dismissed as intangible or reduced to just a set of 'hand wavy' values on your career site. I believe instead that it <em>is</em> tangible and <em>can</em> be managed just like a product. Our overall thinking on this stuff is:
          </p>

        

          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/open-org-approach-culture-design.png"
            alt="OpenOrg Approach to Culture Design"
            caption="Our approach to culture design focuses on intentionality and continuous improvement"
            className="rounded-lg border border-gray-700 my-6"
          />

          <p className="text-gray-300 leading-relaxed">
            An offsite is a fantastic opportunity where you can start to:
          </p>

          <div className="pl-6 space-y-3">
            <div className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1</span>
              <p>Define your culture today, beyond values</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2</span>
              <p>Design (together) your culture of tomorrow</p>
            </div>
          </div>

          <p className="text-gray-300 leading-relaxed">
            So if you're struggling for a half day meaningful off-site activity, grab your sharpies, sticky-notes and magic whiteboard and let's get into it.
          </p>
        </div>
      </PlaybookSection>

      {/* What You'll Need section */}
      <PlaybookSection
        title="What You'll Need"
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <PlaybookCard>
          <div className="space-y-4">
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Lots of sharpies</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Lots of sticky notes</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Lots of magic whiteboard</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>A throwable microphone (optional)</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>A big space with a wall or table per team</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Buy in from leadership that this is worth spending some time on</p>
            </div>
          </div>

          <p className="mt-6 text-gray-400 italic">
            *this can be facilitated remotely as well on a Miro board.
          </p>
        </PlaybookCard>
      </PlaybookSection>

      {/* Obstacles section */}
      <PlaybookSection
        title="Potential Obstacles"
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="h2"
      >
        <ExpandableCallout
          title="Leadership may not see culture as a priority"
          type="warning"
        >
          <p className="text-gray-300 mb-4">
            Some leadership teams switch off immediately if you try to engage them in a conversation about culture, often due to different definitions or understanding of what "culture" means.
          </p>
          <p className="text-gray-300 mb-2 font-medium">Solutions:</p>
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Reframe the conversation to focus on "how we do our best work together" rather than using the loaded term "culture"</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Connect culture work directly to business outcomes and operational excellence</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Present culture as a tangible product that can be managed and improved</p>
            </div>
          </div>
        </ExpandableCallout>

        <ExpandableCallout
          title="Remote or distributed teams may find this challenging"
          type="warning"
          className="mt-4"
        >
          <p className="text-gray-300 mb-4">
            While this activity works best in person, remote teams may struggle with the collaborative aspects.
          </p>
          <p className="text-gray-300 mb-2 font-medium">Solutions:</p>
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Use digital tools like Miro for remote facilitation</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Break into smaller video breakout rooms for deeper discussions</p>
            </div>
            <div className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <p>Consider a hybrid approach with local clusters meeting in person, connected virtually</p>
            </div>
          </div>
        </ExpandableCallout>
      </PlaybookSection>
    </div>
  );
};

export default CultureOffsiteKeyInfo; 